import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Plus, Trash2, Building2 } from "lucide-react";

const CATEGORIES = ["CAT-A", "CAT-B", "CAT-C", "CAT-D"];
const CAT_COLOR = { "CAT-A": "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300", "CAT-B": "bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300", "CAT-C": "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300", "CAT-D": "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" };

export default function Companies() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const { data: companies = [], isLoading } = useQuery({ queryKey: ["companies-directory"], queryFn: async () => (await api.get("/companies/directory")).data });
  const { data: lenders = [] } = useQuery({ queryKey: ["lenders"], queryFn: async () => (await api.get("/lenders")).data });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["companies-directory"] });
  const create = useMutation({
    mutationFn: async (d) => (await api.post("/companies/directory", { name_pattern: d.name_pattern.trim(), category_code: d.category_code, bank_code: d.bank_code || null })).data,
    onSuccess: () => { invalidate(); setForm(null); toast.success("Company added"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/companies/directory/${id}`)).data,
    onSuccess: () => { invalidate(); setToDelete(null); toast.success("Company removed"); },
    onError: (e) => toast.error(apiError(e)),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => [c.name_pattern, c.category_code, c.bank_code].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [companies, search]);

  return (
    <div data-testid="companies-page">
      <PageHeader title="Companies Directory" subtitle="Employer name patterns mapped to salary categories — used to auto-categorise leads for ROI." />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="companies-search-input" placeholder="Search company, category…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white dark:bg-slate-950" />
        </div>
        <Button onClick={() => setForm({ name_pattern: "", category_code: "CAT-A", bank_code: "" })} data-testid="add-company-button" className="bg-blue-700 hover:bg-blue-800 text-white"><Plus className="h-4 w-4 mr-1" />Add Company</Button>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="companies-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Company / Pattern</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Category</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Preferred Bank</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={4}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && filtered.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-10"><Building2 className="h-8 w-8 mx-auto mb-2 opacity-40" />No companies mapped yet. Add one to get started.</TableCell></TableRow>}
            {!isLoading && filtered.map((c) => (
              <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                <TableCell className="font-medium text-slate-900 dark:text-slate-100">{c.name_pattern}</TableCell>
                <TableCell><Badge className={`border-0 ${CAT_COLOR[c.category_code] || "bg-slate-100 text-slate-600"}`}>{c.category_code}</Badge></TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{c.bank_code || "—"}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => setToDelete(c)} data-testid={`delete-company-${c.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-white dark:bg-slate-900" data-testid="company-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add Company</DialogTitle><DialogDescription>Map an employer name pattern to a salary category.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Company Name / Pattern</Label><Input value={form.name_pattern} onChange={(e) => setForm({ ...form, name_pattern: e.target.value })} placeholder="e.g. Tata Consultancy" data-testid="company-name-input" /></div>
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category_code} onValueChange={(v) => setForm({ ...form, category_code: v })}><SelectTrigger data-testid="company-category-select"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Preferred Bank (optional)</Label>
                <Select value={form.bank_code || "none"} onValueChange={(v) => setForm({ ...form, bank_code: v === "none" ? "" : v })}><SelectTrigger data-testid="company-bank-select"><SelectValue placeholder="None" /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{lenders.map((l) => <SelectItem key={l.id || l.code} value={l.code}>{l.name} ({l.code})</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button className="bg-blue-700 hover:bg-blue-800 text-white" disabled={!form?.name_pattern?.trim() || create.isPending} onClick={() => create.mutate(form)} data-testid="company-save-button">{create.isPending ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="company-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Remove company?</AlertDialogTitle><AlertDialogDescription>"{toDelete?.name_pattern}" will be removed from the directory.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => del.mutate(toDelete.id)} data-testid="confirm-delete-company">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
